import React, { useEffect, useState } from 'react';
import './ErrorPage.css';
import UserService from '../../Services/user.service';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const ErrorPage = () => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(null);
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();

    // Determine if the current language is RTL
    const isRTL = i18n.language === 'he' || i18n.language === 'ar';
    const direction = isRTL ? 'rtl' : 'ltr';

    useEffect(() => {
        const init = async () => {
            try {
                const currentUser = await UserService.getCurrentUser();
                const currentToken = await UserService.getCurrentToken();
                if (currentUser && currentToken) {
                    setUser(currentUser);
                    setToken(currentToken);
                } else {
                    navigate("/login");
                }
            } catch (error) {
                // Handle error - maybe navigate to login or show a message
            }
        };

        init();
    }, []);

    return (
        <div className="xtram-error-page" dir={direction}>
            {/* Animated Background Elements */}
            <div className="xtram-error-bg">
                <div className="floating-shape shape-1"></div>
                <div className="floating-shape shape-2"></div>
                <div className="floating-shape shape-3"></div>
                <div className="floating-shape shape-4"></div>
                <div className="floating-shape shape-5"></div>
            </div>

            {/* Main Content */}
            <div className="xtram-error-content">
                {/* 404 Number with Glitch Effect */}
                <div className="xtram-error-number">
                    <span className="glitch" data-text="404">404</span>
                </div>

                {/* Icon Container */}
                <div className="xtram-error-icon">
                    <svg className="icon-search" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
                        <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        <path d="M11 8v6M8 11h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                </div>

                {/* Title */}
                <h1 className="xtram-error-title">{t('error404Title')}</h1>

                {/* Description */}
                <p className="xtram-error-description">
                    {t('error404Description')}
                </p>

                {/* Buttons */}
                <div className="xtram-error-actions">
                    <button 
                        className="xtram-btn xtram-btn-primary" 
                        onClick={() => navigate("/Main")}
                    >
                        <svg className="btn-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M9 22V12h6v10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        {t('error404BackHome')}
                    </button>
                    <button 
                        className="xtram-btn xtram-btn-secondary" 
                        onClick={() => navigate(-1)}
                    >
                        <svg className="btn-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d={isRTL ? "M5 12h14M5 12l7-7M5 12l7 7" : "M19 12H5M5 12l7 7M5 12l7-7"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        {t('error404GoBack')}
                    </button>
                </div>

                {/* Additional Help */}
                <div className="xtram-error-help">
                    <p className="help-text">{t('error404NeedHelp')}</p>
                    <div className="contact-options">
                        <a href="mailto:info@xtram.net" className="contact-link email-link">
                            <svg className="contact-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M22 6l-10 7L2 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            {t('error404Email')}
                        </a>
                        <a href="https://wa.me/972539470430" target="_blank" rel="noopener noreferrer" className="contact-link whatsapp-link">
                            <svg className="contact-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" fill="currentColor"/>
                            </svg>
                            {t('error404WhatsApp')}
                        </a>
                    </div>
                </div>
            </div>

            {/* Bottom Wave Decoration */}
            <div className="wave-decoration">
                <svg viewBox="0 0 1440 320" xmlns="http://www.w3.org/2000/svg">
                    <path fill="url(#wave-gradient)" fillOpacity="1" d="M0,96L48,112C96,128,192,160,288,160C384,160,480,128,576,133.3C672,139,768,181,864,181.3C960,181,1056,139,1152,128C1248,117,1344,139,1392,149.3L1440,160L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
                    <defs>
                        <linearGradient id="wave-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#304FFF" stopOpacity="0.2"/>
                            <stop offset="50%" stopColor="#5C7CFF" stopOpacity="0.3"/>
                            <stop offset="100%" stopColor="#304FFF" stopOpacity="0.2"/>
                        </linearGradient>
                    </defs>
                </svg>
            </div>
        </div>
    );
};

export default ErrorPage;
